import { supabase } from "@/integrations/supabase/client";
import { detectSeriesFromTitle } from "@/lib/seriesUtils";
import { toast } from "sonner";

export async function backfillSeriesFromTitles(batchSize: number = 100) {
  try {
    console.log('[Backfill Series] Starting series detection from titles...');
    
    // Get books without series data
    const { data: booksWithoutSeries, error: fetchError } = await supabase
      .from('books')
      .select('id, title, authors')
      .is('series_name', null)
      .limit(batchSize);
    
    if (fetchError) throw fetchError;
    
    if (!booksWithoutSeries || booksWithoutSeries.length === 0) {
      console.log('[Backfill Series] No books without series data found');
      return { processed: 0, updated: 0 };
    }
    
    console.log(`[Backfill Series] Processing ${booksWithoutSeries.length} books...`);
    
    let updated = 0;
    const updates = [];
    
    for (const book of booksWithoutSeries) {
      const seriesInfo = detectSeriesFromTitle(book.title);
      
      if (seriesInfo) {
        updates.push({
          id: book.id,
          series_name: seriesInfo.seriesName,
          series_position: seriesInfo.position
        });
        updated++;
      }
    }
    
    // Batch update books with series info
    if (updates.length > 0) {
      for (const update of updates) {
        const { error: updateError } = await supabase
          .from('books')
          .update({
            series_name: update.series_name,
            series_position: update.series_position
          })
          .eq('id', update.id);
        
        if (updateError) {
          console.error(`[Backfill Series] Error updating book ${update.id}:`, updateError);
        }
      }
    }
    
    console.log(`[Backfill Series] Updated ${updated} books with series info`);
    
    return {
      processed: booksWithoutSeries.length,
      updated
    };
  } catch (error) {
    console.error('[Backfill Series] Error:', error);
    throw error;
  }
}

export async function triggerSeriesEnrichment(bookIds?: string[]) {
  try {
    console.log('[Series Enrichment] Triggering enrichment...');
    
    const { data, error } = await supabase.functions.invoke('enrich-books', {
      body: {
        action: 'enrich-books',
        bookIds: bookIds || undefined,
        batchSize: bookIds ? bookIds.length : 50
      }
    });
    
    if (error) throw error;
    
    console.log('[Series Enrichment] Enrichment completed:', data);
    return data;
  } catch (error) {
    console.error('[Series Enrichment] Error:', error);
    throw error;
  }
}

export async function backfillUserSeriesTracking() {
  try {
    console.log('[Backfill User Series] Starting user series tracking backfill...');
    
    // Get all user books that are read or reading and have series data
    const { data: userBooks, error: fetchError } = await supabase
      .from('user_books')
      .select(`
        id,
        user_id,
        book_id,
        shelf,
        finished_at,
        books!fk_user_books_book_id!inner (
          series_name,
          series_position,
          series_total,
          authors
        )
      `)
      .in('shelf', ['read', 'reading'])
      .not('books.series_name', 'is', null);
    
    if (fetchError) throw fetchError;
    
    if (!userBooks || userBooks.length === 0) {
      console.log('[Backfill User Series] No user books with series data found');
      return { processed: 0 };
    }
    
    console.log(`[Backfill User Series] Processing ${userBooks.length} user books...`);
    
    // Group by user and series
    const seriesMap = new Map<string, {
      user_id: string;
      series_name: string;
      author_name: string;
      highest_position: number;
      total_books: number | null;
      last_book_id: string;
      last_read_at: string | null;
    }>();
    
    for (const ub of userBooks) {
      const book = (ub.books as any);
      if (!book || !book.series_name || !book.series_position) continue;
      
      const normalizedSeries = book.series_name.toLowerCase().trim();
      const primaryAuthor = book.authors?.[0] || 'Unknown';
      const key = `${ub.user_id}|${normalizedSeries}|${primaryAuthor}`;
      
      const existing = seriesMap.get(key);
      
      if (!existing || book.series_position > existing.highest_position) {
        seriesMap.set(key, {
          user_id: ub.user_id,
          series_name: normalizedSeries,
          author_name: primaryAuthor,
          highest_position: book.series_position,
          total_books: book.series_total,
          last_book_id: ub.book_id,
          last_read_at: ub.finished_at
        });
      }
    }
    
    // Insert or update tracking records
    for (const [_, tracking] of seriesMap) {
      const { error: upsertError } = await supabase
        .from('user_series_tracking')
        .upsert({
          user_id: tracking.user_id,
          series_name: tracking.series_name,
          series_name_display: tracking.series_name, // Will be updated by enrichment
          author_name: tracking.author_name,
          highest_book_position: tracking.highest_position,
          total_books_known: tracking.total_books,
          last_book_id: tracking.last_book_id,
          last_book_read_at: tracking.last_read_at,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,series_name,author_name'
        });
      
      if (upsertError) {
        console.error('[Backfill User Series] Error upserting:', upsertError);
      }
    }
    
    console.log(`[Backfill User Series] Processed ${seriesMap.size} unique series`);
    
    return {
      processed: seriesMap.size
    };
  } catch (error) {
    console.error('[Backfill User Series] Error:', error);
    throw error;
  }
}

export async function runCompleteSeriesBackfill() {
  try {
    toast.info('Starting series data backfill...');
    
    // Step 1: Detect series from titles
    const titleResults = await backfillSeriesFromTitles(500);
    console.log(`Step 1 complete: ${titleResults.updated} books updated from titles`);
    
    // Step 2: Backfill user tracking
    const trackingResults = await backfillUserSeriesTracking();
    console.log(`Step 2 complete: ${trackingResults.processed} series tracked`);
    
    toast.success(`Series backfill complete! Updated ${titleResults.updated} books, tracking ${trackingResults.processed} series`);
    
    return {
      booksUpdated: titleResults.updated,
      seriesTracked: trackingResults.processed
    };
  } catch (error) {
    console.error('[Complete Backfill] Error:', error);
    toast.error('Series backfill failed');
    throw error;
  }
}
